import { type FinanceStatisticsDto } from "@modules/finances/gateway/financeStatisticsDto"
import { type TransactionDetailsDto } from "@modules/finances/gateway/transactionDto"
import { type TransactionTotalsDto } from "@modules/finances/gateway/transactionTotalsDto"
import { type UserFinanceApiGateway } from "@modules/finances/gateway/userFinanceApiGateway"
import { PaymentMethodType, PaymentPlace, TransactionStatus } from "@modules/finances/types/financesType"
import { StripeAccountErrorCode, StripeErrorCode, type StripeErrorsDto } from "@modules/finances/types/stripeErrorTypes"

const _STRIPE_ERROR_CARD_EXPIRED: StripeErrorsDto = {
  paymentErrors: [
    {
      errorCode: StripeErrorCode.CARD_DECLINED,
      last4Digits: "0341",
      subscriptionName: "subscription name",
      programId: "subscription id",
      programBundleId: null,
      boxId: null,
      boxOfferId: null,
      declineCode: null,
      programBundleSubscriptionId: null,
      boxSubscriptionId: null,
      programSubscriptionId: null,
    },
  ],
  accountError: null,
}
const _STRIPE_ERROR_MISSING_DOCUMENT: StripeErrorsDto = {
  paymentErrors: [],
  accountError: StripeAccountErrorCode.IS_MISSING_DOCUMENT,
}
const _STRIPE_ERROR_UNKNOWN_ERROR: StripeErrorsDto = {
  paymentErrors: [],
  accountError: StripeAccountErrorCode.UNKNOWN,
}

export const USER_FINANCE_TEST_DATA = {
  STRIPE_ERRORS: {
    NONE: null,
    CARD_EXPIRED: _STRIPE_ERROR_CARD_EXPIRED,
    MISSING_DOCUMENT: _STRIPE_ERROR_MISSING_DOCUMENT,
    UNKNOWN_ERROR: _STRIPE_ERROR_UNKNOWN_ERROR,
  },
}

const USER_TRANSACTION_DETAILS_DTO: TransactionDetailsDto = {
  transactionId: "in_1Qqt4ZQPhVZSibrNDvCEgjnw",
  paidAt: null,
  isAnonymous: false,
  history: [],
  outOfBandStatus: null,
  payout: null,
  refundable: false,
  date: "2025-02-10T09:14:35Z",
  user: {
    id: "88b77a23-5e0e-4a59-88cf-ba9789729c3f",
    stripeCustomerId: "cus_PLibCXgFEAXOVd",
    firstname: "Nicolas",
    lastname: "Smit",
    username: "NS",
    avatar: {
      url: "http://localhost:9000/users/637d9cfbece64b309ca5c0756dbca9fa",
      hash: "UIFN^]Q-wIoz~qR5nht615SzNdni9aXmOYR*",
    },
    address: null,
    email: "test_fake@example.com",
    phoneNumber: null,
    nationality: null,
    boxMemberNumber: null,
  },
  box: null,
  amount: 2500.0,
  netAmount: 2366.25,
  totalTaxAmount: 0.0,
  fee: 133.75,
  currency: "EUR",
  status: TransactionStatus.SUCCEEDED,
  disputed: false,
  refunded: false,
  refundAmount: 0,
  invoiceId: "in_1Qqt4ZQPhVZSibrN8b2g5d9d",
  invoiceNumber: "E90A8496-1618",
  invoicePdfUrl:
    "https://pay.stripe.com/receipts/invoices/CAcaFwoVYWNjdF8xT1gxMm9RUGhWWlNpYnJOKM77sr0GMgao8y3m3Yo6LBYmqUJtLWFQOz5xh87d9dQ2zlzWCLWje0hizitAmLhME0xBy1EZoMnh0hwJ/pdf?s=ap",
  lines: [
    {
      description: "1 × Tes pack payant PUBLIC modif (at €2,500.00 / every 7 days)",
      netAmount: 2500.0,
      totalAmount: 2500.0,
      taxAmount: 0.0,
      taxPercent: null,
      quantity: 1,
      currency: "EUR",
      periodStart: "2025-02-10T09:14:35Z",
      periodEnd: "2025-02-10T09:14:35Z",
    },
  ],
  discounts: [],
  paymentMethodDetail: {
    type: PaymentMethodType.CARD,
    last4Digits: null,
  },
  disputeDetail: null,
}

export class FakeUserFinanceApi implements UserFinanceApiGateway {
  stripeAccountErrors: StripeErrorsDto | null = USER_FINANCE_TEST_DATA.STRIPE_ERRORS.NONE

  givenStripeAccountErrors(stripeErrors: StripeErrorsDto | null) {
    this.stripeAccountErrors = stripeErrors
  }

  async refundTransaction(_: string): Promise<void> {
    /* empty */
  }

  // eslint-disable-next-line @typescript-eslint/require-await
  async getTransactions(
    _: TransactionStatus | undefined,
    __: PaymentPlace | undefined,
    ___: string | undefined,
    ____: string,
    _____?: number,
    ______ = 10,
  ) {
    return {
      content: [],
      page: 1,
      totalPages: 1,
      totalSize: 0,
    }
  }

  // eslint-disable-next-line @typescript-eslint/require-await
  async getTransactionsHistory(_: string, __?: string, ___ = 10) {
    return {
      hasMore: false,
      page: 1,
      totalPages: 1,
      totalSize: 0,
      data: [],
    }
  }

  // eslint-disable-next-line @typescript-eslint/require-await
  async getStats(_: string): Promise<FinanceStatisticsDto> {
    return {
      counts: {
        succeededCount: 0,
        pendingCount: 0,
        failedCount: 0,
        disputedCount: 0,
        refundedCount: 0,
        atCounterCount: 0,
        onlineCount: 0,
        pendingSepaDebitCount: 0,
      },
      stats: {
        netTotal: 0,
        outOfBandTotal: 0,
        feesTotal: 0,
        refundTotal: 0,
        sepaDebitAmountTotal: 0,
        currency: "EUR",
        activeCustomers: 0,
        currentBalance: {
          pending: 0,
          available: 0,
        },
        lastPayout: null,
        payoutTotalAmount: 0,
      },
      toValidate: {
        failed: {
          count: 0,
          total: 0,
        },
        pending: {
          count: 0,
          total: 0,
        },
        disputed: {
          count: 0,
          total: 0,
        },
      },
      firstPeriod: "",
      isMissingDocumentsError: false,
    }
  }
  // eslint-disable-next-line @typescript-eslint/require-await
  async getTransaction(_: string): Promise<TransactionDetailsDto> {
    return USER_TRANSACTION_DETAILS_DTO
  }

  // eslint-disable-next-line @typescript-eslint/require-await
  async getTransactionTotals(_: string): Promise<TransactionTotalsDto> {
    return {
      succeededTransactionsNumber: 0,
      pendingTransactionsNumber: 0,
      failedTransactionsNumber: 0,
      disputedTransactionsNumber: 0,
      refundedTransactionsNumber: 0,
    }
  }

  // eslint-disable-next-line @typescript-eslint/require-await
  async exportTransactions(_: string, __: string): Promise<string> {
    return ""
  }

  async exportMonthlyInvoices(_: string): Promise<{ blob: Blob; filename: string | undefined }> {
    return Promise.resolve({
      blob: new Blob(),
      filename: "monthly_invoices.zip",
    })
  }

  async exportPayoutTransactions(_payoutId: string): Promise<string> {
    return Promise.resolve("")
  }

  // eslint-disable-next-line @typescript-eslint/require-await
  async getStripeAccountErrors() {
    return this.stripeAccountErrors
  }

  // eslint-disable-next-line @typescript-eslint/require-await
  async getPaymentAuthenticationUrl(_: string, __: string) {
    return ""
  }

  getTransactionPdf(_transactionId: string): Promise<Blob> {
    return Promise.resolve(new Blob())
  }
}
